import * as _ from 'lodash'
import serviceGateway from './serviceGateway';
import store, { getCollection, ModelCollection } from './store';
import * as globalActions from './actions';
import fileModel from './models/fileModel';
import fileUploadStateModel from './models/fileUploadStateModel';
import Queue from './queue';

const processItem = (item) => {

	if(item.processType === 'upload') {
		return uploadItem(item);
	}
	else if(item.processType === 'delete') {
		return deleteItem(item);
	}
	
};

const fileQueue = new Queue({ processor: processItem });

// NOTE: Action returns are only to allow tests to access the promise. DO NOT use return values in runtime code.
export const fetchFilesForJob = params => {
	return serviceGateway.fetchFilesForJob(params)
		.then(result => {
			const collection = getCollection({ collection: ModelCollection.files });

			result.files = _.map(result.files, f => {
				f.rootKey = f.rootKey || params.job.key;
				return fileModel(f);
			});

			collection.withMutations(c => {
				_.forEach(result.files, f => {
					c.set(f.key, f);
				});
			});

			return result; // this is here to support the need to read files from CMS1 for scan management
		})
		.catch(err => {
			globalActions.pushAlert({ type: 'warning', message: err.message || err, isCloseable: true });
		});
};

export const fetchFilesForContractor = params => {
	return serviceGateway.fetchFilesForContractor(params)
		.then(result => {
			const collection = getCollection({ collection: ModelCollection.files });

			collection.withMutations(c => {
				_.forEach(result.files, f => {
					f.rootKey = f.rootKey || params.contractor.key;
					c.set(f.key, fileModel(f));
				});
			});

			return result; // this is here to support the need to read files from CMS1 for scan management
		})
		.catch(err => {
			globalActions.pushAlert({ type: 'warning', message: err.message || err, isCloseable: true });
		});
};

export const enqueueUpload = async params => {
	try {
		const uploadableFile = await serviceGateway.getUploadableFile({ file: { rootKey: params.rootKey, rootType: params.rootType, name: params.file.name || params.name, group: params.group, relatedType: params.relatedType, relatedTypeKey: params.relatedTypeKey } });
		const stateDoc = { status: fileUploadStateModel.STATUS.queued, progress: 0, url: uploadableFile.file.uploadUrl };
		const fileDoc = _.assign(_.omit(uploadableFile.file, ['uploadUrl']), { uploadState: stateDoc });
		const model = fileModel(fileDoc) as Record<string, any>;
		const remoteUrl = model.url;

		model.url = URL.createObjectURL(params.file);

		store().setIn(['model', 'files', model.key], model);

		return new Promise((resolve, reject) => {
			fileQueue.enqueueAfter({ model: model, file: params.file, remoteUrl: remoteUrl, processType: 'upload', resolve, reject });
		})

	} catch(err) {
		globalActions.pushAlert({ type: 'warning', message: err.message || err, isCloseable: true });
	}
};

export const deleteFile = params => {
	if(params.useQueue) {
		return fileQueue.enqueueAfter({ file: params.file, processType: 'delete'});
	}
	else {
		return deleteItem(params);
	}
};

function uploadItem(item) {
	const updateFileState = (stateDoc) => {
		item.model.uploadState = stateDoc;
		store().setIn(['model', 'files', item.model.key], fileModel(item.model));
	};

	const handleProgress = (e) => {
		if (e.lengthComputable) {
			updateFileState({ status: fileUploadStateModel.STATUS.progressing, progress: (e.loaded / e.total), url: item.model.uploadState.url });
		}
	};

	updateFileState({ status: fileUploadStateModel.STATUS.progressing, progress: 0, url: item.model.uploadState.url });

	return serviceGateway.uploadFile({ url: item.model.uploadState.url, file: item.file, progressHandler: handleProgress })
		.then(() => {
			const localUrl = item.model.url;

			item.model.url = item.remoteUrl;
			URL.revokeObjectURL(localUrl);

			updateFileState({ status: fileUploadStateModel.STATUS.complete, progress: 1.0, url: item.model.uploadState.url });
			if(item.resolve) {
				item.resolve(item.model);
			}
		})
		.catch((err) => { // eslint-disable-line handle-callback-err
			updateFileState({ status: fileUploadStateModel.STATUS.failed, progress: 0, url: item.model.uploadState.url });
			if(item.reject) {
				item.reject({err, model: item.model});
			}
		});
}

function deleteItem(item) {
	const keyPath = ['model', 'files', item.file.key];

	store().deleteIn(keyPath);
	return serviceGateway.deleteFile(item)
		.catch(err => {
			store().setIn(keyPath, item.file);
			globalActions.pushAlert({ type: 'warning', message: err.message || err, isCloseable: true });
		});
}